import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../../../../auth/userContextProvider/UserContextProvider";
import { ContractPartyKind, draftContractPartyId } from "../CounterPartiesStep.types";
import { IContractPartyList } from "./ContractPartyList";

export default function useContractPartyListHooks(props: IContractPartyList) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const [draftLegalEntityId, setDraftLegalEntityId] = useState<string | null>(null);
    const [showDraft, setShowDraft] = useState(false);
    const [draftIndex, setDraftIndex] = useState<number | null>(null);

    const isInternal = props.kind === ContractPartyKind.Internal;
    const draftId = draftContractPartyId(props.kind);
    const canEdit = permissions.contractManagementPermissions.permissions.edit && !props.readOnly;

    const header = translateString(isInternal ? "internalContractPartyHeader" : "externalContractPartyHeader");
    const headerToolTip = translateString(isInternal ? "internalContractPartyHeaderTooltip" : "externalContractPartyHeaderTooltip");
    const addPartyText = translateString(isInternal ? "addInternalContractParty" : "addExternalContractParty");
    const partyPlaceholder = translateString(isInternal ? "chooseInternalContractParty" : "chooseExternalContractParty");

    // A legal entity can only be one party on a contract. The saved rows filter their own dropdowns, so the draft has
    // to do the same - otherwise it offers an entity that is already a party and adding it creates a second one.
    const draftPartyOptions = props.partyOptions?.filter((x) => !props.selectedLegalentities.includes(x.id));

    // A group company is only created from group company administration, and the inline dialog always creates an
    // external entity - offering it here would create something the internal dropdown immediately filters away.
    const showAddNewEntityButton = !isInternal;

    function isExpanded(counterPartyId: string) {
        return counterPartyId === props.expandedId;
    }

    function openDraft() {
        openDraftAt(null);
    }

    // The draft stands in for the row it replaces, so clearing a party hands it that row's position rather than
    // letting the card jump to the bottom of the column. One opened from the Add button belongs at the end.
    function openDraftAt(index: number | null) {
        setDraftLegalEntityId(null);
        setDraftIndex(index);
        setShowDraft(true);
        props.onExpand(draftId);
    }

    function closeDraft() {
        setDraftLegalEntityId(null);
        setDraftIndex(null);
        setShowDraft(false);
    }

    // A party means nothing without its legal entity, and the add flow never persists one, so clearing the picker
    // takes the row with it - its sub contractors cascade, the same as removing the party outright. Reopening the
    // draft keeps an empty card on screen to pick a different entity, and navigating away leaves nothing behind.
    async function clearParty(counterPartyId: string) {
        const clearedIndex = props.parties.findIndex((party) => party.id === counterPartyId);

        await props.onRemoveParty(counterPartyId);
        openDraftAt(clearedIndex);
    }

    async function onDraftLegalEntityChange(legalEntityId: string | null) {
        setDraftLegalEntityId(legalEntityId);

        if (legalEntityId) {
            await props.onAddParty(legalEntityId);
            closeDraft();
        }
    }

    return {
        header,
        headerToolTip,
        addPartyText,
        partyPlaceholder,
        showAddNewEntityButton,
        canEdit,
        isExpanded,
        draftId,
        showDraft,
        draftIndex,
        openDraft,
        closeDraft,
        clearParty,
        draftPartyOptions,
        draftLegalEntityId,
        onDraftLegalEntityChange,
    };
}
